'use client'

import { useCallback, useMemo, useEffect, useContext, useState, useRef } from 'react'
import { clsx } from 'clsx'
import { useRouter, usePathname } from 'next/navigation'
import { AppRouterContext } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import { motion, AnimatePresence } from 'framer-motion'
import { Tab, TabGroup, TabList } from '@headlessui/react'
import ListBox from '@/components/ui/ListBox'
import RoomTravelersSelector from '@/components/search/RoomTravelersSelector'
import RoomTravelersSelectorSkeleton from '@/components/search/RoomTravelersSelectorSkeleton'
import YachtDateRangeSelector from '@/components/search/YachtDateRangeSelector'
import DatePickerCustom from '@/components/ui/DatePickerCustom'
import DestinationAutocomplete from './DestinationAutocomplete'
import NumberInput from '@/components/ui/NumberInput'
import PaperAirplaneIcon from '@/components/icons/PaperAirplaneIcon'
import SuitcaseIcon from '@/components/icons/SuitcaseIcon'
import MapPinIcon from '@/components/icons/MapPinIcon'
import TourTabIcon from '@/components/icons/TourTabIcon'
import YachtTabIcon from '@/components/icons/YachtTabIcon'
import ShipTabIcon from '@/components/icons/ShipTabIcon'
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext'
import { useTravelersPreferences } from '@/contexts/TravelersPreferencesContext'
import { usePageUrls } from '@/contexts/PageUrlsContext'
import { DEFAULT_PAX_COUNT } from '@/constants/pricing'
import { UIConstants } from '@/constants/ui'
import type { SearchField, Suggestion } from '@/types/product-search'
import type { PageUrlMap } from '@/types/blog'
import type { ProductType } from '@/lib/api/product-types'
import Image from '@/components/ui/MediaImage'
import { UrlBuilder } from '@/lib/url-builder'
import {
    buildSearchNavigationQuery,
    collectAutocompleteFieldKeys,
    mainSearchApiProductType,
    mainSearchAutocompleteSources,
    mainSearchSubmitButtonLabel,
    mainSearchUrlListingType,
    navigateFromAutocompleteSelection,
    resolvePrimaryAutocompleteSelection,
    withQueryString,
} from '@/lib/main-search'
import { toLocalDateString } from '@/lib/date-utils'
import { useSearchFormState } from './hooks/useSearchFormState'
import { useDateManagement } from './hooks/useDateManagement'
import { useTabManagement } from './hooks/useTabManagement'
import { useCategorySync } from './hooks/useCategorySync'

// SSR-safe router hook wrapper
// Backend'den render edildiğinde App Router context'i olmayabilir
// Hook'ları her zaman çağır (React kuralları) ama context yoksa fallback kullan
function useSafeRouter() {
    const routerContext = useContext(AppRouterContext);
    // Hook'u her zaman çağır (React kuralları)
    // Context yoksa hook hata fırlatacak, bu durumda null döndür
    try {
        const router = useRouter();
        return router;
    } catch (error) {
        // SSR ortamında router context yoksa null döndür
        if (typeof window === 'undefined' || !routerContext) {
            return null;
        }
        throw error;
    }
}

// SSR-safe pathname hook wrapper
function useSafePathname() {
    const routerContext = useContext(AppRouterContext);
    // Hook'u her zaman çağır (React kuralları)
    // Context yoksa hook hata fırlatacak, bu durumda null döndür
    try {
        const pathname = usePathname();
        return pathname;
    } catch (error) {
        // SSR ortamında router context yoksa null döndür
        if (typeof window === 'undefined' || !routerContext) {
            return null;
        }
        throw error;
    }
}

// Sekme başlıkları dil API'sinden (product_type_{key}); kategori sekmelerinde kategori name kullanılır

const TravelersIcon = () => (
    <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="fill-alsero-dark-gray">
        <path d="M15.1811 7.77945C14.7379 9.41938 15.0146 11.6979 16.1374 12.9727C16.3996 13.2703 16.6426 13.5861 16.8643 13.9183C17.7646 15.2672 18.2809 16.8358 18.3576 18.4558C18.3678 18.6042 18.3483 18.7531 18.3002 18.8939C18.252 19.0346 18.1763 19.1643 18.0773 19.2754C17.9784 19.3865 17.8583 19.4767 17.724 19.5407C17.5898 19.6047 17.4441 19.6412 17.2955 19.6482C17.1469 19.6551 16.9984 19.6323 16.8588 19.5811C16.7191 19.5299 16.5911 19.4513 16.4823 19.3499C16.3734 19.2486 16.2859 19.1265 16.2249 18.9908C16.1638 18.8552 16.1305 18.7087 16.1268 18.56C16.0457 16.8394 15.305 15.216 14.0585 14.027C12.8121 12.838 11.1557 12.1747 9.43311 12.1747C7.71052 12.1747 6.05409 12.838 4.80767 14.027C3.56124 15.216 2.82057 16.8394 2.73938 18.56C2.71932 18.8513 2.58602 19.1231 2.36802 19.3173C2.15002 19.5115 1.86465 19.6126 1.57303 19.5989C1.28141 19.5853 1.00671 19.458 0.807769 19.2444C0.608827 19.0307 0.501452 18.7476 0.50864 18.4558C0.585226 16.836 1.10121 15.2675 2.00128 13.9186C2.22286 13.5865 2.46567 13.2708 2.72769 12.9732C3.85043 11.6981 4.12692 9.41954 3.68358 7.77945C3.36429 6.59825 3.4166 5.34736 3.83341 4.19695C4.25022 3.04653 5.01128 2.05243 6.01307 1.34986C7.01487 0.647302 8.20877 0.270386 9.43236 0.270386C10.656 0.270386 11.8499 0.647302 12.8517 1.34986C13.8534 2.05243 14.6145 3.04653 15.0313 4.19695C15.4481 5.34736 15.5004 6.59825 15.1811 7.77945ZM13.156 6.22244C13.156 5.23507 12.7638 4.28814 12.0656 3.58997C11.3674 2.89179 10.4205 2.49956 9.43311 2.49956C8.44574 2.49956 7.49881 2.89179 6.80064 3.58997C6.10247 4.28814 5.71024 5.23507 5.71024 6.22244C5.71024 7.2098 6.10247 8.15673 6.80064 8.8549C7.49881 9.55308 8.44574 9.94531 9.43311 9.94531C10.4205 9.94531 11.3674 9.55308 12.0656 8.8549C12.7638 8.15673 13.156 7.2098 13.156 6.22244Z" />
    </svg>
)

/** Sekme olarak kullanılacak kategori (product type değil, ürün kategorisi) */
export interface CategoryTabItem {
    id: number
    name: string
    slug: string
    type: string
    /** Admin’de ayarlanabilir; boşsa name kullanılır */
    tabTitle?: string
    /** Admin’de ayarlanabilir sekme ikonu URL’i */
    tabIconUrl?: string
}

export interface MainSearchBarProps {
    productType: string
    searchFields: SearchField[]
    language?: string
    categorySlug?: string
    categoryName?: string // Kategori adı (sayfa render olurken geliyor, API çağrısına gerek yok)
    onSearch?: (filters: Record<string, any>) => void
    isLoading?: boolean
    pageUrls?: PageUrlMap
    defaultLanguage?: string
    /** Ürün kategorileri – sekmeler bunlardan oluşur (product type yerine) */
    categoryTabs?: CategoryTabItem[]
    selectedCategoryId?: number | null
    onCategoryTabChange?: (category: CategoryTabItem | null) => void
    /** Eski davranış: product type sekmeleri (categoryTabs yoksa kullanılır) */
    productTypes?: ProductType[]
    activeProductType?: string
    multiTab?: boolean
    productSlug?: string
}

export default function MainSearchBar({
    productType,
    searchFields = [],
    language,
    categorySlug,
    categoryName,
    onSearch,
    isLoading = false,
    pageUrls,
    defaultLanguage,
    categoryTabs = [],
    selectedCategoryId = null,
    onCategoryTabChange,
    productTypes = [],
    activeProductType,
    multiTab = true,
    productSlug,
}: MainSearchBarProps) {
    const { getVariable } = useLanguageVariables()
    const router = useSafeRouter()
    const contextPageUrls = usePageUrls()
    /** Boş `{}` prop layout context’ini ezmesin (ana sayfa arama yönlendirmesi) */
    const effectivePageUrls = useMemo(() => {
        const fromProp =
            pageUrls && typeof pageUrls === 'object' && Object.keys(pageUrls).length > 0
                ? pageUrls
                : undefined
        return fromProp ?? contextPageUrls
    }, [pageUrls, contextPageUrls])
    const { preferences, setDepartureDate: setContextDepartureDate, setProductType } = useTravelersPreferences()
    const pathname = useSafePathname()
    // Language resolution
    const resolvedDefaultLanguage = useMemo(() => 
        defaultLanguage || (typeof window !== 'undefined' 
            ? window.location.pathname.match(/^\/([a-z]{2})\//)?.[1] 
            : null) || 'en',
        [defaultLanguage]
    )
    
    const resolvedLanguage = useMemo(() => 
        language || (typeof window !== 'undefined' 
            ? window.location.pathname.match(/^\/([a-z]{2})\//)?.[1] 
            : null) || resolvedDefaultLanguage,
        [language, resolvedDefaultLanguage]
    )
    
    // URL'den product_type'ı oku
    const urlProductType = useMemo(() => {
        if (!pathname) return null
        
        const segments = pathname.split('/').filter(Boolean)
        if (segments.length === 0) return null
        
        const isLangCode = segments[0]?.length === 2 && /^[a-z]{2}$/.test(segments[0])
        const productTypeSegment = isLangCode ? segments[1] : segments[0]
        
        const validProductTypes = ['tour', 'private-tours', 'group-tours', 'yacht', 'ship_tour', 'ship', 'mice', 'cruise']
        if (productTypeSegment && validProductTypes.includes(productTypeSegment)) {
            if (productTypeSegment === 'ship_tour' || productTypeSegment === 'cruise') {
                return 'ship_tour'
            }
            return productTypeSegment
        }
        
        return null
    }, [pathname])
    
    const resolvedActiveProductType = useMemo(() => 
        urlProductType || activeProductType || productType, 
        [urlProductType, activeProductType, productType]
    )
    
    // Custom hooks - modüler state yönetimi
    const {
        formValues,
        selectedSuggestionTypes,
        selectedSuggestionSlugs,
        selectedSuggestionProductTypes,
        updateFormValue,
        handleAutocompleteSelect,
        setCategoryToForm,
    } = useSearchFormState()

    /** Autocomplete’ten son seçilen öneri — field key kaybına karşı submit kaynağı */
    const lastSelectedSuggestionRef = useRef<Suggestion | null>(null)
    
    const {
        departureDate,
        yachtCheckInDate,
        yachtCheckOutDate,
        setDepartureDate,
        setYachtCheckInDate,
        setYachtCheckOutDate,
        updateFormDates,
    } = useDateManagement(preferences, resolvedActiveProductType, setContextDepartureDate)
    
    const {
        selectedTabIndex,
        tabSearchFields,
        tabLoadingStates,
        activeSearchFields,
        activeTabLoading,
        activeProductTypeForTab,
        handleTabChange,
    } = useTabManagement(
        productTypes,
        searchFields,
        resolvedActiveProductType,
        urlProductType,
        preferences?.product_type,
        isLoading,
        resolvedLanguage,
        multiTab,
        setProductType
    )
    
    const setCategoryToFormSynced = useCallback(
        (fieldKey: string, slug: string, name?: string) => {
            const listingFromUrl =
                urlProductType === 'private-tours' ||
                urlProductType === 'group-tours' ||
                urlProductType === 'yacht' ||
                urlProductType === 'ship_tour'
                    ? urlProductType
                    : null
            lastSelectedSuggestionRef.current = {
                id: 0,
                name: name || slug,
                slug,
                type: 'category',
                product_type: listingFromUrl || 'private-tours',
            }
            setCategoryToForm(fieldKey, slug, name)
        },
        [setCategoryToForm, urlProductType],
    )

    const { categoryLoadedRef } = useCategorySync(
        pathname,
        categorySlug,
        categoryName,
        activeSearchFields,
        selectedSuggestionSlugs,
        setCategoryToFormSynced,
    )
    
    // Sekmeler: önce ürün kategorileri (categoryTabs), yoksa product type'lar
    const useCategoryTabs = categoryTabs.length > 0
    const resolvedMultiTab = useMemo(() => multiTab && (useCategoryTabs ? categoryTabs.length > 1 : productTypes.length > 0), [multiTab, useCategoryTabs, categoryTabs.length, productTypes.length])

    // Hydration: sunucu ve ilk istemci render'da loading aynı olsun (disabled/button metni uyumsuzluğu önlenir)
    const [hasMounted, setHasMounted] = useState(false)
    useEffect(() => {
        setHasMounted(true)
    }, [])

    // Blok kullanımında (onCategoryTabChange yok) seçili kategoriyi yerel state ile yönet
    const [localSelectedCategoryId, setLocalSelectedCategoryId] = useState<number | null>(null)
    const effectiveSelectedCategoryId = onCategoryTabChange
        ? selectedCategoryId
        : (localSelectedCategoryId ?? selectedCategoryId ?? categoryTabs[0]?.id ?? null)
    useEffect(() => {
        if (!onCategoryTabChange && categoryTabs.length > 0 && localSelectedCategoryId === null)
            setLocalSelectedCategoryId(selectedCategoryId ?? categoryTabs[0]?.id ?? null)
    }, [categoryTabs, selectedCategoryId, onCategoryTabChange, localSelectedCategoryId])

    // Kategori sekmeleri: API sırası (sort_order); type_key = slug (render uyumu)
    const visibleTabs = useMemo(() => {
        if (useCategoryTabs) {
            const list = (categorySlug || productSlug) ? categoryTabs.filter(c => c.slug === categorySlug) : categoryTabs
            return list.map(c => ({ ...c, type_key: c.slug }))
        }
        if (categorySlug || productSlug) return productTypes.filter(pt => pt.type_key === resolvedActiveProductType)
        return productTypes
    }, [useCategoryTabs, categoryTabs, categorySlug, productSlug, productTypes, resolvedActiveProductType])

    /** Mobil: 2–4 sekme → tam genişlik “segmented” ızgara; 5+ → büyük pill + yatay kaydırma */
    const searchTabsMobile = useMemo(() => {
        const n = visibleTabs.length
        const segmented = n >= 2 && n <= 4
        const gridCols =
            n === 2
                ? 'max-sm:grid-cols-2'
                : n === 3
                  ? 'max-sm:grid-cols-3'
                  : n === 4
                    ? 'max-sm:grid-cols-2 max-sm:auto-rows-fr'
                    : ''
        return { n, segmented, gridCols }
    }, [visibleTabs])

    const visibleTabIndex = useMemo(() => {
        if (!resolvedMultiTab || visibleTabs.length === 0) return 0
        if (visibleTabs.length === 1) return 0
        if (useCategoryTabs) {
            const idx = visibleTabs.findIndex((t) => 'id' in t && t.id === effectiveSelectedCategoryId)
            return idx >= 0 ? idx : 0
        }
        const activeType = resolvedActiveProductType || productTypes[selectedTabIndex]?.type_key
        const index = visibleTabs.findIndex((pt) => pt.type_key === activeType)
        return index >= 0 ? index : (selectedTabIndex < visibleTabs.length ? selectedTabIndex : 0)
    }, [resolvedMultiTab, visibleTabs, useCategoryTabs, effectiveSelectedCategoryId, resolvedActiveProductType, productTypes, selectedTabIndex])

    const activeCategoryTab = visibleTabs[visibleTabIndex] as CategoryTabItem | undefined
    const activeCategoryType = useCategoryTabs && activeCategoryTab ? activeCategoryTab.type : null

    // Kategori sekmelerinde her tip (tour, yacht) için API'den form alanları – 2. resimdeki gibi tek form stili
    const [categoryTabSearchFields, setCategoryTabSearchFields] = useState<Record<string, SearchField[]>>({})
    const [categoryTabLoadingStates, setCategoryTabLoadingStates] = useState<Record<string, boolean>>({})

    useEffect(() => {
        if (!useCategoryTabs || !categoryTabs.length) return
        const firstTabType = (categoryTabs[0] as CategoryTabItem).type
        setCategoryTabSearchFields((prev) => ({ ...prev, [firstTabType]: searchFields }))
    }, [useCategoryTabs, searchFields, categoryTabs])

    useEffect(() => {
        if (!useCategoryTabs || !activeCategoryType || !categoryTabs.length) return
        const firstTabType = (categoryTabs[0] as CategoryTabItem).type
        // İlk sekme: parent (HomeHeaderSearchBar) searchFields getirir; asla ek istek atma / loading açma (cache gecikmesinde de spinner olmasın)
        if (activeCategoryType === firstTabType) {
            setCategoryTabSearchFields((prev) => (prev[activeCategoryType] ? prev : { ...prev, [activeCategoryType]: searchFields }))
            return
        }
        // Sadece zaten veri varsa tekrar istek atma. categoryTabLoadingStates dependency'de olmamalı:
        // loading=true set edilince effect tekrar çalışıp cleanup'ta cancelled=true oluyor, API cevabı state'i güncellemiyor ve loading sonsuz kalıyordu.
        if (Object.prototype.hasOwnProperty.call(categoryTabSearchFields, activeCategoryType)) return
        let cancelled = false
        setCategoryTabLoadingStates((prev) => ({ ...prev, [activeCategoryType]: true }))
        import('@/lib/api/product-search').then(({ getProductFilters }) => getProductFilters(activeCategoryType, resolvedLanguage))
            .then((config) => {
                if (!cancelled) setCategoryTabSearchFields((prev) => ({ ...prev, [activeCategoryType]: config.search_fields || [] }))
            })
            .catch(() => {
                if (!cancelled) setCategoryTabSearchFields((prev) => ({ ...prev, [activeCategoryType]: [] }))
            })
            .finally(() => {
                if (!cancelled) setCategoryTabLoadingStates((prev) => ({ ...prev, [activeCategoryType]: false }))
            })
        return () => { cancelled = true }
    }, [useCategoryTabs, activeCategoryType, resolvedLanguage, categoryTabs, searchFields, categoryTabSearchFields])

    const apiProductType = useMemo(
        () => mainSearchApiProductType(useCategoryTabs ? activeCategoryTab : null, productType),
        [useCategoryTabs, activeCategoryTab, productType],
    )
    const urlListingType = useMemo(
        () => mainSearchUrlListingType(useCategoryTabs ? activeCategoryTab : null, productType),
        [useCategoryTabs, activeCategoryTab, productType],
    )
    const effectiveProductTypeForTab = apiProductType

    /** Submit sırasında render edilen formla aynı alan seti (kategori sekmelerinde tab’a özel API alanları) */
    const searchFieldsForSubmit = useMemo(() => {
        if (useCategoryTabs && activeCategoryType) {
            return categoryTabSearchFields[activeCategoryType] ?? searchFields ?? []
        }
        return activeSearchFields?.length ? activeSearchFields : (searchFields ?? [])
    }, [useCategoryTabs, activeCategoryType, categoryTabSearchFields, searchFields, activeSearchFields])

    useEffect(() => {
        updateFormDates(updateFormValue)
    }, [departureDate, yachtCheckInDate, yachtCheckOutDate, effectiveProductTypeForTab, updateFormDates, updateFormValue])

    // Helper: URL yönlendirme (handleVisibleTabChange'tan önce tanımlanmalı - dependency sırası)
    const navigateToUrl = useCallback((url: string) => {
        if (!url || url === '#') {
            if (process.env.NODE_ENV === 'development') {
                console.error('[MainSearchBar] Navigation skipped: invalid URL', url)
            }
            return
        }
        if (router) {
            router.push(url)
        } else if (typeof window !== 'undefined') {
            window.location.href = url
        }
    }, [router])
    
    const handleVisibleTabChange = useCallback((index: number) => {
        if (index < 0 || index >= visibleTabs.length) return
        if (useCategoryTabs) {
            const cat = visibleTabs[index] as CategoryTabItem
            if (onCategoryTabChange) onCategoryTabChange(cat ?? null)
            else setLocalSelectedCategoryId(cat?.id ?? null)
            return
        }
        const selectedProductType = (visibleTabs[index] as ProductType)?.type_key
        if (!selectedProductType) return
        const realIndex = productTypes.findIndex(pt => pt.type_key === selectedProductType)
        if (realIndex >= 0) handleTabChange(realIndex)
    }, [visibleTabs, useCategoryTabs, onCategoryTabChange, productTypes, handleTabChange])
    
    // Helper: URL oluştur
    const buildUrl = useCallback((templateKey: string, slug: string, productTypeOverride?: string) => {
        if (!effectivePageUrls || !resolvedLanguage) {
            if (process.env.NODE_ENV === 'development') {
                console.error('MainSearchBar: pageUrls and language are required for URL building');
            }
            return '#';
        }
        
        const builder = new UrlBuilder(effectivePageUrls, resolvedLanguage, resolvedDefaultLanguage);
        const productType = productTypeOverride ?? effectiveProductTypeForTab
        
        if (templateKey.includes('-cat')) {
            return builder.buildCategoryUrl(productType, slug);
        } else if (templateKey.includes('-detail')) {
            return builder.buildProductDetailUrl(productType, slug);
        }
        
        return builder.buildUrl(templateKey, { item: slug });
    }, [effectivePageUrls, resolvedLanguage, resolvedDefaultLanguage, effectiveProductTypeForTab])

    const appendFilterToSearchParams = useCallback((params: URLSearchParams, key: string, value: unknown) => {
        if (value == null || value === '') return
        if (Array.isArray(value)) {
            value.forEach((item) => {
                if (item != null && item !== '') params.append(`${key}[]`, String(item))
            })
            return
        }
        params.set(key, String(value))
    }, [])
    
    const handleAutocompleteSearch = useCallback(
        (fieldKey: string, autocompleteSource?: string, overrideProductType?: string) => async (query: string): Promise<Suggestion[]> => {
            if (query.length < 2) return []

            try {
                const { getAutocompleteSuggestions } = await import('@/lib/api/product-search')
                // API catalog type (tour/yacht); listing (private-tours) yalnızca URL için
                const typeForApi = overrideProductType || apiProductType
                const sources = mainSearchAutocompleteSources(autocompleteSource, typeForApi)
                // root_category_id gönderme: Private Tours sekmesi (id=1) Destinations
                // altındaki cappadocia-tours vb. kategorileri filtreleyip sadece şehir bırakıyordu →
                // yanlış /private-tours?...&station_city=... URL’si
                const params = {
                    product_type: typeForApi,
                    field_key: fieldKey,
                    query,
                    language: resolvedLanguage,
                    limit: 10,
                    ...(sources.includes(',') ? { sources } : { source: sources }),
                }
                return await getAutocompleteSuggestions(params, getVariable)
            } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                    console.error('[MainSearchBar] Autocomplete error:', error)
                }
                return []
            }
        },
        [apiProductType, resolvedLanguage, getVariable],
    )

    const buildCommonSearchFilters = useCallback(() => {
        const firstRoom = preferences?.rooms?.[0]
        const searchFilters: Record<string, any> = { ...formValues }

        if (effectiveProductTypeForTab === 'yacht') {
            if (yachtCheckInDate) {
                searchFilters.check_in_date = toLocalDateString(yachtCheckInDate)
            }
            if (yachtCheckOutDate) {
                searchFilters.check_out_date = toLocalDateString(yachtCheckOutDate)
            }
        } else if (departureDate) {
            const dateStr = toLocalDateString(departureDate)
            searchFilters.departure_date = dateStr
            searchFilters.check_in_date = dateStr
        }

        if (firstRoom?.adults && firstRoom.adults > 0) {
            searchFilters.pax_count = firstRoom.adults
            if (firstRoom.children && firstRoom.children > 0) {
                searchFilters.child_count = firstRoom.children
                if (firstRoom.childAges?.length) {
                    searchFilters.child_ages = JSON.stringify(firstRoom.childAges)
                }
            }
        }

        return searchFilters
    }, [preferences, formValues, effectiveProductTypeForTab, yachtCheckInDate, yachtCheckOutDate, departureDate])

    const handleSubmit = useCallback(() => {
        const autocompleteKeys = collectAutocompleteFieldKeys(searchFieldsForSubmit, selectedSuggestionTypes)
        let primary = resolvePrimaryAutocompleteSelection(
            autocompleteKeys,
            selectedSuggestionTypes,
            selectedSuggestionSlugs,
            selectedSuggestionProductTypes,
            formValues,
        )

        // Ref her zaman taze; React state gecikse bile kategori/ürün path’i koru
        const last = lastSelectedSuggestionRef.current
        if (last && (last.type === 'category' || last.type === 'product') && last.slug) {
            if (
                primary.kind === 'none' ||
                primary.kind === 'station_city' ||
                primary.slug === last.slug ||
                primary.kind !== last.type
            ) {
                primary = {
                    kind: last.type,
                    slug: last.slug,
                    productType: last.product_type ?? primary.productType,
                    fieldKey: primary.fieldKey,
                }
            }
        }

        const searchFilters = buildCommonSearchFilters()

        // Kategori → {main_cat_slug}/{cat_slug} | Ürün → {main_cat_slug}/{product_slug}
        // Tarih & pax cookie/session’da; temiz path, query yok
        if ((primary.kind === 'category' || primary.kind === 'product') && primary.slug) {
            const listingHint =
                primary.productType ||
                urlListingType ||
                activeCategoryTab?.type ||
                activeCategoryTab?.slug
            const target = navigateFromAutocompleteSelection({
                kind: primary.kind,
                slug: primary.slug,
                suggestionProductType: listingHint,
                tab: activeCategoryTab ?? null,
                pageUrls: effectivePageUrls,
                language: resolvedLanguage,
                defaultLanguage: resolvedDefaultLanguage,
            })
            if (target && target !== '#') {
                navigateToUrl(target)
                return
            }
        }

        if (primary.kind === 'station_city' && primary.slug) {
            searchFilters.station_city = primary.slug
        }

        // Kategori sekmesi seçiliyse arama filtresine ekle
        if (effectiveSelectedCategoryId != null) {
            searchFilters.category_ids = [effectiveSelectedCategoryId]
        }

        if (onSearch) {
            onSearch(searchFilters)
            return
        }
        // Ana sayfa: sekme landing’i (private-tours / group-tours / yacht) — filtre query ile
        if (useCategoryTabs && categoryTabs.length > 0 && effectivePageUrls && resolvedLanguage) {
            const selectedCat = categoryTabs.find((c) => c.id === effectiveSelectedCategoryId) ?? categoryTabs[0]
            const builder = new UrlBuilder(effectivePageUrls, resolvedLanguage, resolvedDefaultLanguage)
            const isLanding = selectedCat.slug === selectedCat.type
            const categoryUrl = isLanding
                ? builder.buildUrl(selectedCat.type, {})
                : builder.buildCategoryUrl(selectedCat.type, selectedCat.slug)
            if (categoryUrl !== '#') {
                // Sadece tarih/pax; destination metni URL’ye yazılmaz (kategori seçimi yukarıda path’e gider)
                const navQuery = buildSearchNavigationQuery(searchFilters, appendFilterToSearchParams)
                navigateToUrl(withQueryString(categoryUrl, navQuery))
            }
        }
    }, [
        preferences,
        departureDate,
        yachtCheckInDate,
        yachtCheckOutDate,
        formValues,
        onSearch,
        searchFieldsForSubmit,
        selectedSuggestionTypes,
        selectedSuggestionSlugs,
        selectedSuggestionProductTypes,
        activeCategoryTab,
        effectiveSelectedCategoryId,
        navigateToUrl,
        useCategoryTabs,
        categoryTabs,
        effectivePageUrls,
        resolvedLanguage,
        resolvedDefaultLanguage,
        appendFilterToSearchParams,
        buildCommonSearchFilters,
        effectiveProductTypeForTab,
        urlListingType,
    ])

    const submitButtonLabel = useMemo(() => {
        const autocompleteKeys = collectAutocompleteFieldKeys(searchFieldsForSubmit, selectedSuggestionTypes)
        const primary = resolvePrimaryAutocompleteSelection(
            autocompleteKeys,
            selectedSuggestionTypes,
            selectedSuggestionSlugs,
            selectedSuggestionProductTypes,
            formValues,
        )
        const listingForLabel =
            primary.productType ||
            urlListingType ||
            activeCategoryType ||
            effectiveProductTypeForTab
        return mainSearchSubmitButtonLabel(primary.kind, listingForLabel, getVariable)
    }, [
        searchFieldsForSubmit,
        selectedSuggestionTypes,
        selectedSuggestionSlugs,
        selectedSuggestionProductTypes,
        formValues,
        urlListingType,
        activeCategoryType,
        effectiveProductTypeForTab,
        getVariable,
    ])
    
    const createAutocompleteOnChange = useCallback((fieldKeys: string[]) => (value: string) => {
        fieldKeys.forEach(key => updateFormValue(key.trim(), value))
    }, [updateFormValue])
    
    /** Birleşik autocomplete alanlarında seçimi tüm key’lere yaz (destination + product_id) */
    const createAutocompleteOnSelect = useCallback((fieldKey: string) => (suggestion: Suggestion | null) => {
        const keys = fieldKey.split(',').map((k) => k.trim()).filter(Boolean)
        const keysToWrite = keys.length > 0 ? keys : [fieldKey]
        lastSelectedSuggestionRef.current = suggestion
        if (suggestion) {
            keysToWrite.forEach((key) => {
                handleAutocompleteSelect(key)(suggestion)
                updateFormValue(key, suggestion.name)
            })
        } else {
            keysToWrite.forEach((key) => handleAutocompleteSelect(key)(null))
        }
    }, [handleAutocompleteSelect, updateFormValue])
    
    // Field renderer
    const renderField = useCallback((field: SearchField, index: number, productTypeKey?: string) => {
        const fieldValue = formValues[field.field_key] || ''
        const isYachtTab = productTypeKey === 'yacht'
        
        switch (field.field_type) {
            case 'autocomplete':
                return null
                
            case 'dropdown':
                // Yacht tab için port dropdown özel placeholder ve icon
                const isPortField = isYachtTab && (field.field_key === 'port' || field.field_key === 'departure_port')
                const portPlaceholder = isPortField ? getVariable('yacht_select_port', 'Select Port') : undefined
                
                // Port field için varsayılan seçim yapılmamalı (boş başlamalı)
                const dropdownValue = isPortField ? (fieldValue || '') : fieldValue
                
                // Options mapping: backend'den gelen format {key, label, value} veya {key, label}
                const mappedOptions = field.options?.map((opt, idx) => {
                    const option = opt as { key: string; label: string; value?: string; title?: string }
                    return {
                        title: option.label || option.title || String(option.value || option.key || ''),
                        value: option.value || option.key || String(idx),
                        id: parseInt(String(option.key || option.value || idx)) || idx,
                        icon: null,
                    }
                }) || []
                
                // Port field için boş bir seçenek ekle (en başa)
                const finalOptions = isPortField 
                    ? [
                        {
                            title: portPlaceholder || getVariable('yacht_select_port', 'Select Port'),
                            value: '',
                            id: -1,
                            icon: null,
                        },
                        ...mappedOptions
                    ]
                    : mappedOptions
                
                return (
                    <ListBox
                        key={field.field_key}
                        title={field.label}
                        icon={field.icon || null}
                        value={dropdownValue}
                        onChange={(value) => updateFormValue(field.field_key, value)}
                        options={finalOptions}
                    />
                )
                
            case 'date':
                // Yacht tab'ında date field'ları render edilmez (YachtDateRangeSelector kullanılır)
                if (isYachtTab) return null
                
                if (field.field_key.includes('check_in') || field.field_key === 'departure_date') {
                    return (
                        <motion.div
                            key={field.field_key}
                            className="w-full"
                            variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                        >
                            <DatePickerCustom
                                label={field.label || getVariable('search_departure_date', 'Çıkış Tarihi')}
                                selected={departureDate}
                                onSelect={(date) => {
                                    setDepartureDate(date)
                                    const dateStr = date ? toLocalDateString(date) : undefined
                                    if (dateStr) {
                                        updateFormValue('departure_date', dateStr)
                                        updateFormValue('check_in_date', dateStr)
                                    }
                                    setContextDepartureDate(date || null)
                                }}
                                disabled={{ before: new Date() }}
                            />
                        </motion.div>
                    )
                }
                if (field.field_key.includes('check_out')) return null
                
                return (
                    <motion.div
                        key={field.field_key}
                        className="w-full"
                        variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                        <DatePickerCustom
                            label={field.label}
                            selected={fieldValue ? new Date(fieldValue) : undefined}
                            onSelect={(date) => updateFormValue(field.field_key, date ? toLocalDateString(date) : '')}
                            disabled={{ before: new Date() }}
                        />
                    </motion.div>
                )
                
            case 'number':
                // Yacht tab için total_cabin NumberInput component'i kullan
                if (isYachtTab && (field.field_key === 'total_cabin' || field.field_key === 'cabin_count')) {
                    return (
                        <NumberInput
                            key={field.field_key}
                            label={field.label || 'Total Cabin'}
                            icon={<SuitcaseIcon />}
                            value={typeof fieldValue === 'number' ? fieldValue : (fieldValue ? parseInt(fieldValue, 10) : 0)}
                            onChange={(value) => updateFormValue(field.field_key, value)}
                            min={UIConstants.CABIN_MIN_COUNT}
                            max={UIConstants.CABIN_MAX_COUNT}
                            step={UIConstants.CABIN_STEP}
                            isRequired={field.is_required}
                        />
                    )
                }
                // Diğer number field'lar için normal input
                return (
                    <motion.div
                        key={field.field_key}
                        className="w-full"
                        variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                        {field.label && (
                            <label htmlFor={field.field_key} className="text-alsero-dark-gray text-lg mb-1 block">
                                {field.label}
                                {field.is_required && <span className="text-red-500 ml-1">*</span>}
                            </label>
                        )}
                        <input
                            id={field.field_key}
                            type="number"
                            autoComplete="off"
                            value={fieldValue}
                            onChange={(e) => updateFormValue(field.field_key, e.target.value)}
                            placeholder={getVariable(`search_${field.field_key}_placeholder`, `Enter ${field.label?.toLowerCase() || ''}...`)}
                            className="relative flex flex-row justify-between items-center px-6 py-3 bg-white rounded-lg w-full min-h-[60px] text-alsero-dark-gray text-lg outline-none border-none placeholder:text-alsero-light-gray placeholder:opacity-60"
                            required={field.is_required}
                        />
                    </motion.div>
                )
                
            case 'text':
                return (
                    <motion.div
                        key={field.field_key}
                        className="w-full"
                        variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                        {field.label && (
                            <label htmlFor={field.field_key} className="text-alsero-dark-gray text-lg mb-1 block">
                                {field.label}
                                {field.is_required && <span className="text-red-500 ml-1">*</span>}
                            </label>
                        )}
                        <input
                            id={field.field_key}
                            type="text"
                            autoComplete="off"
                            value={fieldValue}
                            onChange={(e) => updateFormValue(field.field_key, e.target.value)}
                            placeholder={getVariable(`search_${field.field_key}_placeholder`, `Enter ${field.label?.toLowerCase() || ''}...`)}
                            className="relative flex flex-row justify-between items-center px-6 py-3 bg-white rounded-lg w-full min-h-[60px] text-alsero-dark-gray text-lg outline-none border-none placeholder:text-alsero-light-gray placeholder:opacity-60"
                            required={field.is_required}
                        />
                    </motion.div>
                )
                
            default:
                return null
        }
    }, [formValues, departureDate, updateFormValue, getVariable, setContextDepartureDate])
    
    // Form content renderer (shared between multi-tab and single-tab)
    const renderFormContent = useCallback((fields: SearchField[], productTypeKey: string, loading: boolean) => {
        const isYachtTab = productTypeKey === 'yacht'
        
        // Field'lar backend'den dinamik olarak geliyor (ProductTypeField modelinden is_searchable = true olanlar)
        // Manuel field ekleme yapmıyoruz, backend'den gelmesi gerekiyor
        
        const autocompleteFields = fields.filter(f => f?.field_type === 'autocomplete')
        const mergedField = autocompleteFields.length ? (() => {
            const allSources = [...new Set(autocompleteFields.map(f => f.autocomplete_source).filter(Boolean))].join(',')
            return {
                ...autocompleteFields[0],
                autocomplete_source: allSources || autocompleteFields[0].autocomplete_source,
                field_key: autocompleteFields.map(f => f.field_key).join(','),
            }
        })() : null
        
        const hasTravelersField = fields.some(f => f.field_key === 'travelers' || f.field_key === 'pax_count')
        
        // Yacht tab için özel placeholder ve icon helper'ları
        const getYachtAutocompletePlaceholder = (fieldKey: string) => {
            if (fieldKey === 'yacht_id' || fieldKey === 'yacht_code') {
                return getVariable('yacht_search_placeholder', 'Search Yacht, Marina...')
            }
            return getVariable(`search_${fieldKey}_placeholder`, 'Search Region/Destination or Product ID...')
        }
        
        const getYachtAutocompleteLabel = (fieldKey: string, defaultLabel: string) => {
            if (fieldKey === 'yacht_id' || fieldKey === 'yacht_code') {
                return getVariable('yacht_search_label', 'Yacht / Marina')
            }
            return defaultLabel
        }
        
        const getYachtAutocompleteIcon = (fieldKey: string) => {
            if (fieldKey === 'yacht_id' || fieldKey === 'yacht_code') {
                return <PaperAirplaneIcon />
            }
            return null
        }
        
        /**
         * <lg: stack / 2 kolon
         * lg+xl+2xl: tek satır grid — destination fr ile boşluğu doldurur, buton alta düşmez
         */
        const destinationShell = clsx(
            'min-w-0 w-full',
            'col-span-1 sm:col-span-2 lg:col-span-1',
        )
        const midFieldShell = 'min-w-0 w-full col-span-1'
        const yachtDateShell = clsx(
            'min-w-0 w-full',
            'col-span-1 sm:col-span-2 lg:col-span-1',
        )
        const submitShell = clsx(
            'w-full min-w-0',
            'col-span-1 sm:col-span-2 lg:col-span-1',
        )

        const layoutClass = clsx(
            'w-full min-w-0 items-end gap-3',
            'grid grid-cols-1 sm:grid-cols-2',
            isYachtTab
                ? 'lg:grid-cols-[minmax(0,1fr)_minmax(12rem,20rem)_auto] xl:grid-cols-[minmax(0,1fr)_minmax(14rem,22rem)_auto]'
                : 'lg:grid-cols-[minmax(0,1fr)_minmax(10rem,15rem)_minmax(8rem,12rem)_auto] xl:grid-cols-[minmax(0,1fr)_minmax(12rem,16rem)_minmax(9rem,13rem)_auto]',
        )

        return (
            <div className={layoutClass}>
                {mergedField && (
                    <div className={destinationShell}>
                        <DestinationAutocomplete
                            key={mergedField.field_key}
                            fieldKey={mergedField.field_key.split(',')[0]}
                            label={isYachtTab ? getYachtAutocompleteLabel(mergedField.field_key.split(',')[0], mergedField.label) : mergedField.label}
                            icon={isYachtTab ? getYachtAutocompleteIcon(mergedField.field_key.split(',')[0]) : <MapPinIcon />}
                            placeholder={isYachtTab ? getYachtAutocompletePlaceholder(mergedField.field_key.split(',')[0]) : getVariable(`search_${mergedField.field_key.split(',')[0]}_placeholder`, 'Search Region/Destination or Product ID...')}
                            value={formValues[mergedField.field_key.split(',')[0]] || formValues[mergedField.field_key] || ''}
                            onChange={createAutocompleteOnChange(mergedField.field_key.split(','))}
                            onSelect={createAutocompleteOnSelect(mergedField.field_key)}
                            getSuggestions={handleAutocompleteSearch(mergedField.field_key.split(',')[0], mergedField.autocomplete_source, productTypeKey)}
                            isLoading={loading}
                            isRequired={mergedField.is_required}
                        />
                    </div>
                )}

                {fields.map((field, idx) => {
                    if (field.field_key === 'travelers' || field.field_key === 'pax_count') {
                        if (isYachtTab) return null
                        if (loading) {
                            return (
                                <div key={field.field_key} className={midFieldShell}>
                                    <RoomTravelersSelectorSkeleton
                                        title={field.label}
                                        icon={<TravelersIcon />}
                                    />
                                </div>
                            )
                        }
                        return (
                            <div key={field.field_key} className={midFieldShell}>
                                <RoomTravelersSelector
                                    title={field.label}
                                    departureDate={departureDate || null}
                                    icon={<TravelersIcon />}
                                />
                            </div>
                        )
                    }
                    if (field.field_type === 'autocomplete') return null
                    const fieldContent = renderField(field, idx, productTypeKey)
                    if (!fieldContent) return null
                    return (
                        <div key={field.field_key || idx} className={midFieldShell}>
                            {fieldContent}
                        </div>
                    )
                })}

                {!hasTravelersField && !isYachtTab && (
                    <div className={midFieldShell}>
                        {loading ? (
                            <RoomTravelersSelectorSkeleton
                                title={getVariable('search_travelers', 'Number of Travelers')}
                                icon={<TravelersIcon />}
                            />
                        ) : (
                            <RoomTravelersSelector
                                title={getVariable('search_travelers', 'Number of Travelers')}
                                departureDate={departureDate || null}
                                icon={<TravelersIcon />}
                            />
                        )}
                    </div>
                )}

                {isYachtTab && (
                    <div className={yachtDateShell}>
                        <YachtDateRangeSelector
                            key="yacht-date-range"
                            title={getVariable('yacht_rental_period', 'Rental Period')}
                            icon={
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-alsero-dark-gray">
                                    <path d="M6 2V4M14 2V4M3 6H17M4 4H16C16.5523 4 17 4.44772 17 5V17C17 17.5523 16.5523 18 16 18H4C3.44772 18 3 17.5523 3 17V5C3 4.44772 3.44772 4 4 4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            }
                            checkInDate={yachtCheckInDate}
                            checkOutDate={yachtCheckOutDate}
                            onCheckInChange={(date) => {
                                setYachtCheckInDate(date)
                                if (date) {
                                    const dateStr = toLocalDateString(date)
                                    updateFormValue('check_in_date', dateStr)
                                    updateFormValue('departure_date', dateStr)
                                    setContextDepartureDate(date)
                                } else {
                                    updateFormValue('check_in_date', '')
                                    updateFormValue('departure_date', '')
                                }
                            }}
                            onCheckOutChange={(date) => {
                                setYachtCheckOutDate(date)
                                if (date) {
                                    updateFormValue('check_out_date', toLocalDateString(date))
                                } else {
                                    updateFormValue('check_out_date', '')
                                }
                            }}
                        />
                    </div>
                )}

                <motion.button
                    type="submit"
                    disabled={loading}
                    className={clsx(
                        submitShell,
                        'bg-alsero-first text-base sm:text-lg font-semibold rounded-xl sm:rounded-lg whitespace-nowrap text-white px-6 sm:px-8 lg:px-8 xl:px-10',
                        'min-h-[52px] sm:min-h-[60px] lg:min-h-[60px] cursor-pointer hover:bg-alsero-dark-gray transition-colors duration-300',
                        'disabled:opacity-50 disabled:cursor-not-allowed relative z-10 flex items-center justify-center shadow-md shadow-alsero-first/25',
                    )}
                    variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                >
                    {loading
                        ? getVariable('search_loading', 'Loading...')
                        : submitButtonLabel}
                </motion.button>
            </div>
        )
    }, [formValues, departureDate, yachtCheckInDate, yachtCheckOutDate, createAutocompleteOnChange, createAutocompleteOnSelect, handleAutocompleteSearch, renderField, getVariable, preferences?.rooms, preferences?.departure_date, updateFormValue, setContextDepartureDate, submitButtonLabel, setYachtCheckInDate, setYachtCheckOutDate])
    
    // Loading skeleton
    if (activeTabLoading && !activeSearchFields?.length) {
        return (
            <section className="container mx-auto w-full min-w-0 px-3 sm:px-4 lg:-mt-[110px] font-sans relative z-30">
                <motion.div
                    key={`main-search-bar-loading-${productType}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-alsero-soft-white rounded-2xl p-3 sm:p-5 md:p-6 lg:px-6 lg:py-5 xl:px-8 xl:py-6 min-w-0"
                >
                    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3 w-full min-w-0">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="w-full sm:flex-1 sm:min-w-0 min-h-[60px] h-[60px] bg-white/20 rounded-lg animate-pulse" />
                        ))}
                        <div className="h-[60px] w-full sm:w-[min(180px,100%)] sm:flex-shrink-0 bg-white/20 rounded-lg animate-pulse" />
                        <div className="h-[60px] w-full sm:w-[min(180px,100%)] sm:flex-shrink-0 bg-alsero-first/30 rounded-lg animate-pulse" />
                    </div>
                </motion.div>
            </section>
        )
    }
    
    // Multi-tab render (kategori sekmeleri veya product type sekmeleri)
    // Ana sayfada tüm sekmeler; alt sayfada sadece o sayfaya ait sekme (visibleTabs zaten filtrelenmiş)
    if (resolvedMultiTab && visibleTabs.length > 0) {
        return (
            <section className="container mx-auto w-full min-w-0 px-3 sm:px-4 lg:-mt-[110px] font-sans relative z-30">
                <TabGroup selectedIndex={visibleTabIndex} onChange={handleVisibleTabChange}>
                    {(visibleTabs.length > 1 || (useCategoryTabs && (categorySlug || productSlug))) && (
                        <TabList
                            className={clsx(
                                'min-w-0 mx-0 px-0 sm:mx-4 md:mx-8 lg:mx-10',
                                searchTabsMobile.segmented
                                    ? clsx(
                                          searchTabsMobile.gridCols,
                                          'max-sm:grid max-sm:w-full max-sm:gap-px max-sm:overflow-hidden max-sm:rounded-2xl max-sm:border max-sm:border-white/20 max-sm:bg-white/15 max-sm:p-px max-sm:shadow-xl',
                                          'sm:flex sm:flex-wrap sm:justify-center sm:gap-2 sm:overflow-visible sm:rounded-none sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none',
                                      )
                                    : clsx(
                                          'flex flex-row flex-nowrap justify-start gap-2 overflow-x-auto overscroll-x-contain scroll-smooth pb-1 pt-0.5',
                                          'snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                                          'touch-pan-x sm:touch-auto sm:flex-wrap sm:justify-center sm:gap-2 sm:overflow-visible sm:pb-0 sm:snap-none',
                                      ),
                            )}
                        >
                            {visibleTabs.map((pt) => (
                                <Tab
                                    key={pt.type_key}
                                    type="button"
                                    onClick={(e) => e.stopPropagation()}
                                    className={clsx(
                                        'group shrink-0 snap-start',
                                        'flex cursor-pointer transition-all duration-300 ease-out',
                                        'border border-white/10 bg-alsero-dark-gray text-white shadow-sm',
                                        'data-selected:border-alsero-soft-white data-selected:bg-white data-selected:text-alsero-text-dark data-selected:shadow-md',
                                        'data-hover:bg-white/95 data-hover:text-alsero-text-dark',
                                        'focus:not-data-focus:outline-none data-focus:outline data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-white/80',
                                        searchTabsMobile.segmented
                                            ? 'max-sm:flex max-sm:min-h-[4.75rem] max-sm:flex-col max-sm:items-center max-sm:justify-center max-sm:gap-2 max-sm:rounded-none max-sm:border-0 max-sm:px-2 max-sm:py-3 max-sm:shadow-none sm:flex-row sm:items-center sm:gap-3 sm:rounded-t-xl sm:border sm:px-4 sm:py-3 md:gap-4 md:rounded-t-2xl md:px-7 md:py-4'
                                            : 'flex-row items-center gap-2.5 px-4 py-3.5 sm:gap-3 sm:rounded-t-xl sm:px-6 sm:py-3.5 md:gap-4 md:rounded-t-2xl md:px-7 md:py-4',
                                    )}
                                >
                                    {useCategoryTabs ? (
                                        (() => {
                                            const cat = pt as CategoryTabItem
                                            const iconUrl = cat.tabIconUrl ?? ''
                                            const isSvg = iconUrl && (iconUrl.toLowerCase().endsWith('.svg') || iconUrl.includes('.svg?'))
                                            if (isSvg) {
                                                return (
                                                    <span className="flex h-7 w-7 shrink-0 items-center justify-center text-white transition-colors duration-300 sm:h-[29px] sm:w-[29px] group-data-[selected]:text-alsero-dark-gray group-data-[hover]:text-alsero-dark-gray">
                                                        <img src={iconUrl} alt="" width={29} height={29} className="h-full w-full object-contain" />
                                                    </span>
                                                )
                                            }
                                        })()
                                    ) : (pt as ProductType).type_key === 'tour' || (pt as ProductType).type_key === 'private-tours' || (pt as ProductType).type_key === 'group-tours' ? (
                                        <span className="flex h-7 w-7 shrink-0 items-center justify-center text-white transition-colors duration-300 sm:h-[29px] sm:w-[29px] group-data-[selected]:text-alsero-dark-gray group-data-[hover]:text-alsero-dark-gray">
                                            <TourTabIcon className="h-full w-full" />
                                        </span>
                                    ) : (pt as ProductType).type_key === 'yacht' ? (
                                        <span className="flex h-7 w-7 shrink-0 items-center justify-center text-white transition-colors duration-300 sm:h-[29px] sm:w-[29px] group-data-[selected]:text-alsero-dark-gray group-data-[hover]:text-alsero-dark-gray">
                                            <YachtTabIcon className="h-full w-full" />
                                        </span>
                                    ) : (pt as ProductType).type_key === 'ship_tour' ? (
                                        <span className="flex h-7 w-7 shrink-0 items-center justify-center text-white transition-colors duration-300 sm:h-[29px] sm:w-[29px] group-data-[selected]:text-alsero-dark-gray group-data-[hover]:text-alsero-dark-gray">
                                            <ShipTabIcon className="h-full w-full" />
                                        </span>
                                    ) : (pt as ProductType).icon_url ? (
                                        <Image
                                            src={(pt as ProductType).icon_url ?? ''}
                                            alt={pt.name}
                                            width={29}
                                            height={29}
                                            className="h-7 w-7 shrink-0 fill-white transition-all duration-300 ease-in-out sm:h-[29px] sm:w-[29px] group-data-[selected]:fill-alsero-dark-gray group-data-[hover]:fill-alsero-dark-gray"
                                            unoptimized={(pt as ProductType).icon_url?.startsWith('/api/media/proxy/')}
                                        />
                                    ) : (
                                        <div className="h-7 w-7 shrink-0 sm:h-[29px] sm:w-[29px]" />
                                    )}
                                    <span
                                        className={clsx(
                                            'font-semibold tracking-tight text-white group-data-[selected]:text-alsero-text-dark group-data-[hover]:text-alsero-text-dark',
                                            searchTabsMobile.segmented
                                                ? 'max-sm:w-full max-sm:text-center text-sm leading-snug line-clamp-3 sm:max-w-[12rem] sm:text-left sm:text-base md:text-lg lg:text-xl'
                                                : 'max-w-[11rem] text-left text-sm leading-snug line-clamp-2 sm:line-clamp-none sm:max-w-none sm:text-base md:text-lg lg:text-xl',
                                        )}
                                    >
                                        {useCategoryTabs ? pt.name : getVariable(`product_type_${(pt as ProductType).type_key}`, pt.name)}
                                    </span>
                                </Tab>
                            ))}
                        </TabList>
                    )}
                    <div className="bg-alsero-soft-white rounded-2xl p-3 sm:p-5 md:p-6 lg:px-6 lg:py-5 xl:px-8 xl:py-6 min-w-0 overflow-visible shadow-lg sm:shadow-none ring-1 ring-black/5 sm:ring-0">
                        <AnimatePresence mode="wait">
                            {visibleTabs.map((pt, idx) => {
                                const isActive = idx === visibleTabIndex
                                const tabType = useCategoryTabs ? (pt as CategoryTabItem).type : (pt as ProductType).type_key
                                const tabFields = useCategoryTabs
                                    ? (categoryTabSearchFields[tabType] ?? searchFields)
                                    : (tabSearchFields[(pt as ProductType).type_key] || [])
                                const tabLoading = useCategoryTabs
                                    ? (categoryTabLoadingStates[tabType] ?? false)
                                    : (tabLoadingStates[(pt as ProductType).type_key] || false)
                                const formProductType = useCategoryTabs ? tabType : (pt as ProductType).type_key
                                
                                if (!isActive && visibleTabs.length > 1) return null
                                
                                return (
                                    <motion.div
                                        key={pt.type_key}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.15 }}
                                    >
                                        <motion.form
                                            autoComplete="off"
                                            onSubmit={(e) => {
                                                e.preventDefault()
                                                handleSubmit()
                                            }}
                                            className="w-full min-w-0"
                                        >
                                            {renderFormContent(tabFields.length ? tabFields : activeSearchFields, formProductType, hasMounted ? tabLoading : false)}
                                        </motion.form>
                                    </motion.div>
                                )
                            })}
                        </AnimatePresence>
                    </div>
                </TabGroup>
            </section>
        )
    }
    
    // Single-tab render
    return (
        <section className="container mx-auto w-full min-w-0 px-3 sm:px-4 lg:-mt-[110px] font-sans relative z-30">
            <div className="bg-alsero-soft-white rounded-2xl p-3 sm:p-5 md:p-6 lg:px-6 lg:py-5 xl:px-8 xl:py-6 min-w-0 overflow-visible shadow-lg sm:shadow-none ring-1 ring-black/5 sm:ring-0">
                <motion.form
                    key={`main-search-bar-${activeProductTypeForTab}`}
                    autoComplete="off"
                    onSubmit={(e) => {
                        e.preventDefault()
                        handleSubmit()
                    }}
                    className="w-full min-w-0"
                    initial="hidden"
                    animate="visible"
                    variants={{
                        hidden: { opacity: 0 },
                        visible: { opacity: 1, transition: { delayChildren: 0 } },
                    }}
                >
                    {renderFormContent(activeSearchFields, activeProductTypeForTab, hasMounted ? activeTabLoading : false)}
                </motion.form>
            </div>
        </section>
    )
}
